// 編集ロック（仕様書「同時編集の防止」）：編集を始めたらかけ、30 秒ごとに延ばし、閉じたら外す。
// ほかの人が持っていれば「〇〇さんが編集中」と出して操作を止める。運行管理者は引き継げる
import { useEffect, useRef, useState } from 'react';
import { RULES, can, type LockInfo } from '@torapon/shared';
import { api, HttpError } from './api';
import { useNames } from './directory';
import { useMe } from './me';
import { useLive } from './realtime';
import { useToast } from '../components/ui';

/** いまかかっているロックの一覧（鍵の印を出すため） */
export function useLocks() {
  const [locks, setLocks] = useState<LockInfo[]>([]);
  const load = () => api.get<{ locks: LockInfo[] }>('/v1/office/locks').then((r) => setLocks(r.locks)).catch(() => {});
  useEffect(() => { void load(); }, []);
  useLive(['locks'], load);
  return locks;
}

export type EditLock = { key: string | null; mine: boolean; by: string | null; takeover: () => Promise<void> };

/** key を編集している間ロックを持つ。key が null なら何もしない */
export function useEditLock(key: string | null): EditLock {
  const me = useMe();
  const toast = useToast();
  const [state, setState] = useState<{ mine: boolean; by: string | null }>({ mine: false, by: null });
  const held = useRef(false);
  const blocked = useRef(false);
  const acquire = async (k: string) => {
    try {
      await api.post<LockInfo>('/v1/office/locks', { key: k });
      if (blocked.current) toast('編集できるようになりました');
      held.current = true; blocked.current = false;
      setState({ mine: true, by: me?.id ?? null });
    } catch (e) {
      const by = e instanceof HttpError && e.status === 423 ? ((e.detail as { by?: string } | undefined)?.by ?? null) : null;
      if (held.current && by) toast('編集が引き継がれました。保存されていない変更は消えます');
      held.current = false; blocked.current = !!by;
      setState({ mine: false, by });
    }
  };
  useEffect(() => {
    if (!key) { setState({ mine: false, by: null }); return; }
    held.current = false; blocked.current = false;
    void acquire(key);
    const t = setInterval(() => void acquire(key), RULES.lockHeartbeatSec * 1000);
    return () => {
      clearInterval(t);
      if (held.current) void api.del(`/v1/office/locks/${encodeURIComponent(key)}`).catch(() => {});
      held.current = false;
    };
  }, [key]);
  // ほかの人が外した・引き継いだら、すぐに確かめ直す
  useLive(['locks'], () => { if (key) void acquire(key); });
  const takeover = async () => {
    if (!key) return;
    try { await api.post(`/v1/office/locks/${encodeURIComponent(key)}/takeover`, {}); held.current = true; blocked.current = false; setState({ mine: true, by: me?.id ?? null }); toast('編集を引き継ぎました'); }
    catch (e) { toast(e instanceof HttpError ? e.message : '引き継げませんでした'); }
  };
  return { key, mine: !key || state.mine, by: state.by, takeover };
}

/** ほかの人が編集中のときの知らせ（what：「佐藤 A の 11/18 の予定」など） */
export function LockBanner({ lock, what }: { lock: EditLock; what: string }) {
  const names = useNames();
  const me = useMe();
  const [asking, setAsking] = useState(false);
  if (!lock.key || lock.mine || !lock.by) return null;
  return (
    <div role="alert" style={{ border: '1px solid var(--alert)', borderRadius: 10, padding: 10, display: 'grid', gap: 6, background: '#fff5f4' }}>
      <b className="err">{names.staff(lock.by)}さんが編集中のため、{what}は変更できません</b>
      <small className="muted">見るのは自由です。編集が終わると自動で変更できるようになります（操作がないまま 2 分で切れます）。</small>
      {me && can(me.role, 'register') && (asking
        ? <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><small>{names.staff(lock.by)}さんの保存されていない変更は消えます。引き継ぎますか？</small><button className="btn sm" onClick={() => setAsking(false)}>やめる</button><button className="btn sm danger" onClick={() => { setAsking(false); void lock.takeover(); }}>引き継ぐ</button></span>
        : <button className="btn sm" style={{ justifySelf: 'start' }} onClick={() => setAsking(true)}>編集を引き継ぐ</button>)}
    </div>
  );
}
